import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BookmarkPlus, Loader2, MessageCircleQuestion, Send, Sparkles, Wrench, ListChecks, MonitorSmartphone } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useLocation } from "react-router-dom";
import { apiClient, useAuth } from "@/lib/auth";
import { useAllyCurrent } from "@/lib/allyContext";
import { readScreen } from "./screen";
import BuildList, { PromptBox } from "./BuildList";
import { ScopeToggle, TraceCard } from "./Trace";

const err = (e) => e?.response?.data?.detail || "Ally couldn't answer right now";
const MODES = [["fix", "Fix or improve", Wrench], ["ask", "Ask a question", MessageCircleQuestion], ["list", "Build List", ListChecks]];

function Options({ m, onPick, picked }) {
  return (
    <div className="space-y-2" data-testid="ally-options">
      {m.options.map((o) => (
        <button key={o.id} type="button" onClick={() => onPick(o)} disabled={!!picked} data-testid={`ally-option-${o.id}`}
          className={`w-full text-left p-3 rounded-md border transition-colors ${picked?.id === o.id ? "border-brand-cyan bg-brand-cyan/10" : "border-white/10 bg-white/[0.02] hover:border-brand-cyan/50"} disabled:cursor-default`}>
          <div className="flex items-center gap-2"><span className="text-sm text-slate-100 font-medium">{o.title}</span>
            {m.recommended === o.id && <span data-testid={`ally-recommended-${o.id}`} className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-mono text-[9px] uppercase tracking-[0.14em]">Recommended</span>}</div>
          <div className="text-xs text-slate-300 mt-1">{o.how}</div>
          {o.where && <div className="text-[11px] text-amber-200/90 mt-1">Change made in: {o.where}</div>}
          <div className="text-[11px] text-slate-500 mt-1">Tradeoff: {o.tradeoff}</div>
        </button>))}
      {m.why && <p className="text-[11px] text-slate-400">{m.why}</p>}
    </div>
  );
}

function Chat({ mode, getScreen, onSaved, msgs, setMsgs, pick, setPick, scope, setScope }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  const problem = msgs.filter((m) => m.role === "user").map((m) => m.content).join(" / ");
  const send = async () => {
    const q = text.trim(); if (!q) return;
    const next = [...msgs, { role: "user", content: q, scope }]; setMsgs(next); setText(""); setBusy(true); setPick(null);
    const screen = getScreen();
    try {
      const { data } = await apiClient.post("/ally/chat", { mode, scope, screen, messages: next.map(({ role, content }) => ({ role, content: content.slice(0, 4000) })) });
      const content = mode === "ask" ? data.answer : [data.reply, data.question].filter(Boolean).join(" ");
      setMsgs([...next, { role: "ally", content, scope: data.scope, trace: data.trace, ...(mode === "fix" ? { options: data.options, recommended: data.recommended, why: data.why, engine: data.engine } : {}) }]);
    } catch (e) { toast.error(err(e)); setMsgs(next); } finally { setBusy(false); }
  };
  const choose = async (m, o) => {
    setPick({ id: o.id, msg: m, option: o, prompt: null }); setBusy(true);
    const screen = getScreen();
    try {
      const { data } = await apiClient.post("/ally/prompt", { screen, problem, option: o, scope: m.scope || "screen", trace: m.trace || {} });
      setPick({ id: o.id, msg: m, option: o, prompt: data.prompt });
    }
    catch (e) { toast.error(err(e)); setPick(null); } finally { setBusy(false); }
  };
  const save = async () => {
    try {
      const plat = pick.msg.scope === "platform";
      await apiClient.post("/ally/build-list", { screen: getScreen(), engine: (plat && pick.msg.trace?.root_cause?.engine) || pick.msg.engine || "Platform", problem,
        option: pick.option, prompt: pick.prompt, scope: pick.msg.scope || "screen", trace: plat ? pick.msg.trace : {} });
      toast.success("Saved to the build list"); setPick({ ...pick, saved: true }); onSaved();
    } catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="flex flex-col gap-3 min-h-0">
      {!msgs.length && <p data-testid={`ally-${mode}-intro`} className="text-sm text-slate-400">{mode === "fix" ? "Tell me what feels off or what should work better here. I'll give you up to three ways to fix it." : "Ask how something works, what a number means, or why it looks this way."}</p>}
      <div className="space-y-3" data-testid={`ally-${mode}-thread`}>
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
            <div className={`max-w-[92%] text-sm whitespace-pre-wrap rounded-lg px-3 py-2 ${m.role === "user" ? "bg-brand-cyan/15 text-slate-100" : "bg-white/[0.04] text-slate-200"}`} data-testid={`ally-msg-${m.role}-${i}`}>
              {m.scope && <span data-testid={`ally-msg-scope-${i}`} className="block mb-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">{m.scope === "platform" ? "Platform" : "Screen"}</span>}{m.content}</div>
            {m.role === "ally" && m.scope === "platform" && <TraceCard trace={m.trace} testid={`ally-trace-${i}`} />}
            {m.options?.length > 0 && <div className="mt-2"><Options m={m} picked={pick?.msg === m ? pick : null} onPick={(o) => choose(m, o)} /></div>}
          </div>))}
        {busy && <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="ally-thinking"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Ally is thinking…</div>}
        {pick?.prompt && (
          <div className="space-y-2" data-testid="ally-prompt-result">
            <div className="text-xs text-slate-400">Agent prompt for "{pick.option.title}" — ready to copy.</div>
            <PromptBox text={pick.prompt} testid="ally-prompt" />
            <button type="button" disabled={pick.saved} onClick={save} data-testid="ally-save"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-400/50 text-emerald-300 font-mono text-[10px] uppercase tracking-[0.14em] hover:bg-emerald-500/10 disabled:opacity-50">
              <BookmarkPlus className="h-3.5 w-3.5" /> {pick.saved ? "Saved" : "Save to my build list"}</button>
          </div>)}
        <div ref={end} />
      </div>
      <div className="sticky bottom-0 bg-[#0b0f1f] pt-2 space-y-1.5">
      <div className="flex items-center gap-2 text-[10px] text-slate-500"><ScopeToggle scope={scope} setScope={setScope} testid={`ally-${mode}-scope`} />
        <span>{scope === "platform" ? "Traces it across every engine" : "Just this screen — fastest"}</span></div>
      <div className="flex gap-2">
        <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={mode === "fix" ? "e.g. The paychecks list is hard to scan…" : "e.g. Why is Money out higher than last week?"} data-testid={`ally-${mode}-input`}
          className="flex-1 resize-none rounded-md bg-navy-950 border border-white/10 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-brand-cyan/60" />
        <button type="button" disabled={busy || !text.trim()} onClick={send} data-testid={`ally-${mode}-send`} className="px-3 rounded-md border border-brand-cyan/50 text-brand-cyan hover:bg-brand-cyan/10 disabled:opacity-40"><Send className="h-4 w-4" /></button>
      </div>
      </div>
    </div>
  );
}

const HIDDEN = ["/admin/login", "/admin/auth/callback", "/admin/forgot-password", "/admin/reset-password"];
const EMPTY = { fix: { msgs: [], pick: null }, ask: { msgs: [], pick: null } };
export const allyAllowed = (user, path) => user?.role === "super_admin" && (path.startsWith("/admin") || path === "/os") && !HIDDEN.some((h) => path.startsWith(h));

/** Ally OS: super-admin assistant, mounted once for every staff screen. Context = the focused window, else the page. */
export default function AllyOS() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const current = useAllyCurrent(pathname);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("fix");
  const [tick, setTick] = useState(0);
  const [scope, setScope] = useState("platform");
  const [threads, setThreads] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("ally_threads")) || EMPTY; } catch { return EMPTY; }
  });
  useEffect(() => { try { sessionStorage.setItem("ally_threads", JSON.stringify({ fix: { msgs: threads.fix.msgs, pick: null }, ask: { msgs: threads.ask.msgs, pick: null } })); } catch { /* full */ } }, [threads]);
  const thread = (m) => ({ msgs: threads[m].msgs, pick: threads[m].pick,
    setMsgs: (v) => setThreads((t) => ({ ...t, [m]: { ...t[m], msgs: typeof v === "function" ? v(t[m].msgs) : v } })),
    setPick: (v) => setThreads((t) => ({ ...t, [m]: { ...t[m], pick: typeof v === "function" ? v(t[m].pick) : v } })) });
  const cur = useRef(current);
  cur.current = current;
  useEffect(() => {
    const h = () => { setMode("list"); setOpen(true); };
    window.addEventListener("ally:build-list", h);
    return () => window.removeEventListener("ally:build-list", h);
  }, []);
  if (!allyAllowed(user, pathname)) return null;
  const getScreen = () => {
    const s = readScreen();
    const c = cur.current;
    return { ...s, kind: c.kind === "desk" || c.kind === "page" ? s.kind : c.kind, title: c.title || s.title, context: c };
  };
  return (
    <div className="print:hidden" data-testid="ally-root">
      {!open && <button type="button" onClick={() => setOpen(true)} data-testid="ally-launcher" title="Ally OS" style={{ pointerEvents: "auto" }}
        className="fixed bottom-24 right-3 sm:bottom-5 sm:right-5 z-[9990] h-11 sm:h-12 px-3 sm:pl-3 sm:pr-4 rounded-full bg-[#0b1220] border border-brand-cyan/50 text-brand-cyan shadow-[0_10px_40px_rgba(0,200,255,0.25)] inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform duration-300 print:hidden">
        <Sparkles className="h-5 w-5" /><span className="font-mono text-[11px] uppercase tracking-[0.2em] hidden sm:inline">Ally OS</span>
      </button>}
      <Sheet open={open} onOpenChange={setOpen} modal={false}>
        <SheetContent side="right" data-testid="ally-panel" onInteractOutside={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}
          className="z-[9991] bg-[#0b0f1f] text-slate-100 border-white/10 w-full sm:max-w-md h-full overflow-y-auto flex flex-col gap-4 print:hidden shadow-[-20px_0_60px_rgba(0,0,0,0.5)]">
          <SheetHeader><SheetTitle className="font-serif text-slate-100 flex items-center gap-2"><Sparkles className="h-4 w-4 text-brand-cyan" /> Ally OS</SheetTitle></SheetHeader>
          <div data-testid="ally-screen" className="flex items-center gap-2 text-[11px] text-slate-400"><MonitorSmartphone className="h-3.5 w-3.5 text-slate-500" /> Looking at: <span className="text-slate-200" data-testid="ally-looking-at">{current.title}{current.step ? ` > ${current.step}` : ""}</span></div>
          <div className="flex gap-1 p-1 rounded-md bg-white/[0.03] border border-white/10" role="tablist">
            {MODES.map(([k, l, Icon]) => (
              <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => setMode(k)} data-testid={`ally-mode-${k}`}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 rounded font-mono text-[10px] uppercase tracking-[0.14em] transition-colors ${mode === k ? "bg-brand-cyan/15 text-brand-cyan" : "text-slate-400 hover:text-slate-200"}`}>
                <Icon className="h-3.5 w-3.5" />{l}</button>))}
          </div>
          <div className={mode === "fix" ? "" : "hidden"}><Chat mode="fix" getScreen={getScreen} onSaved={() => setTick((n) => n + 1)} scope={scope} setScope={setScope} {...thread("fix")} /></div>
          <div className={mode === "ask" ? "" : "hidden"}><Chat mode="ask" getScreen={getScreen} onSaved={() => {}} scope={scope} setScope={setScope} {...thread("ask")} /></div>
          {mode === "list" && <BuildList tick={tick} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
